import { FC, useEffect, useState } from 'react';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import * as S from './styled';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

const COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'AE', name: 'UAE', dialCode: '+971', flag: '🇦🇪' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
];

function parseE164(value: string): { country: Country; national: string } | null {
  if (!value || !value.startsWith('+')) return null;
  // Sort by dial code length descending to match longer codes first (e.g. +234 before +2)
  const sorted = [...COUNTRIES].sort((a, b) => {
    return b.dialCode.length - a.dialCode.length;
  });
  for (const country of sorted) {
    if (value.startsWith(country.dialCode)) {
      return { country, national: value.slice(country.dialCode.length) };
    }
  }
  return null;
}

interface PhoneInputProps {
  label: string;
  value?: string;
  onChange: (e164: string) => void;
  error?: string;
  marginbottom?: number;
  placeholder?: string;
}

const PhoneInput: FC<PhoneInputProps> = ({
  label,
  value = '',
  onChange,
  error,
  marginbottom,
  placeholder = 'e.g. 4155552671',
}) => {
  const defaultCountry = COUNTRIES[0]; // US +1
  const [country, setCountry] = useState<Country>(defaultCountry);
  const [national, setNational] = useState('');

  useEffect(() => {
    if (value) {
      const parsed = parseE164(value);
      if (parsed) {
        setCountry(parsed.country);
        setNational(parsed.national);
      }
    }
  }, []);

  const handleCountryChange = (e: SelectChangeEvent<unknown>) => {
    const selected =
      COUNTRIES.find((c) => {
        return c.code === e.target.value;
      }) || defaultCountry;
    setCountry(selected);
    if (national) onChange(`${selected.dialCode}${national}`);
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    setNational(digits);
    onChange(digits ? `${country.dialCode}${digits}` : '');
  };

  return (
    <S.Wrapper marginbottom={marginbottom}>
      <S.Label hasError={!!error}>{label}</S.Label>
      <S.InputRow hasError={!!error}>
        <S.CountrySelect value={country.code} onChange={handleCountryChange}>
          {COUNTRIES.map((c) => {
            return (
              <MenuItem key={c.code} value={c.code}>
                {c.flag} {c.dialCode} ({c.name})
              </MenuItem>
            );
          })}
        </S.CountrySelect>
        <S.NumberInput
          type="tel"
          value={national}
          onChange={handleNationalChange}
          placeholder={placeholder}
        />
      </S.InputRow>
      {error && <S.ErrorText>{error}</S.ErrorText>}
    </S.Wrapper>
  );
};

export default PhoneInput;
